import React, { FC, ReactNode, createContext, useEffect, useRef, useState } from 'react';
import { clearFromLocalStorage, loadFromLocalStorage, saveToLocalStorage } from '@utils/storage';
import {
    SERVICES_CART_DISPLAY_WORKERS_KEY,
    SERVICES_CART_KEY,
    SERVICES_CART_OBSERVATIONS_KEY,
    SERVICES_CART_PAYMENT_METHOD_KEY,
    SERVICES_CART_PAYMENT_PHONE_NUMBER_KEY,
    SERVICES_CART_SCHEDULE_DAY_KEY,
    SERVICES_CART_SCHEDULE_TIME_KEY,
    SERVICES_CART_SCHEDULE_TYPE_KEY,
    SERVICES_CART_STORE_KEY,
    SERVICES_CART_STORE_NAME_KEY,
    SERVICES_CART_WORKER_KEY,
} from '@constants';

export interface CartService {
    uuid: string;
    quantity: number;
    checked: boolean;
}

interface Worker {
    uuid: string;
    name: string;
}

type ServicesCartContextProps = {
    // store
    storeUuid: string | null;
    storeName: string | null;
    setStoreUuid: (uuid: string) => void;
    setStoreName: (name: string) => void;
    // services
    initialClickedService: string | null;
    services: CartService[] | null;
    checkService: (uuid: string) => void;
    toggleService: (uuid: string) => void;
    updateServiceAmount: (uuid: string, quantity: number) => void;
    removeService: (uuid: string) => void;
    setInitialServices: (services: { uuid: string; checked?: boolean }[]) => void;
    resetServices: () => void;
    // schedule type
    scheduleType: 'preBook' | 'bookAndPay' | null;
    setScheduleType: (type: 'preBook' | 'bookAndPay') => void;
    // workers
    displayWorkerStep: boolean;
    worker: Worker | null;
    setDisplayWorkerStep: (value: boolean) => void;
    setWorker: (worker: Worker | null) => void;
    // schedule
    day: string | null;
    setDay: (value: string | null) => void;
    timeSlot: string | null;
    setTimeSlot: (value: string | null) => void;
    observations: string | null;
    setObservations: (value: string) => void;
    // payment
    paymentMethod: string | null;
    setPaymentMethod: (value: string | null) => void;
    paymentPhoneNumber: string | null;
    setPaymentPhoneNumber: (value: string | null) => void;
};

export const ServicesCartContext = createContext<ServicesCartContextProps>({
    storeUuid: null,
    storeName: null,
    setStoreUuid: () => {},
    setStoreName: () => {},
    initialClickedService: null,
    services: [],
    checkService: () => {},
    toggleService: () => {},
    updateServiceAmount: () => {},
    removeService: () => {},
    setInitialServices: () => {},
    resetServices: () => {},
    scheduleType: null,
    setScheduleType: () => {},
    displayWorkerStep: false,
    worker: null,
    setDisplayWorkerStep: () => {},
    setWorker: () => {},
    day: null,
    setDay: () => {},
    timeSlot: null,
    setTimeSlot: () => {},
    observations: null,
    setObservations: () => {},
    paymentMethod: null,
    setPaymentMethod: () => {},
    paymentPhoneNumber: null,
    setPaymentPhoneNumber: () => {},
});

type ServicesCartContextProvider = {
    children: ReactNode;
};

export const ServicesCartProvider: FC<ServicesCartContextProvider> = ({ children }) => {
    // to help distinguish between:
    // null on initial load (wanting to load from localStorage),
    // null intentionally set (wanting to save null to localStorage or remove the key).
    const isFirstRender = useRef(true);

    // store
    const [storeUuid, setStoreUuid] = useState<string | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_STORE_KEY);
            setStoreUuid(initialValue);
        } else if (storeUuid === null) {
            clearFromLocalStorage(SERVICES_CART_STORE_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_STORE_KEY, storeUuid);
        }
    }, [storeUuid]);

    const [storeName, setStoreName] = useState<string | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_STORE_NAME_KEY);
            setStoreName(initialValue);
        } else if (storeName === null) {
            clearFromLocalStorage(SERVICES_CART_STORE_NAME_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_STORE_NAME_KEY, storeName);
        }
    }, [storeName]);

    // services
    const [services, setServices] = useState<CartService[] | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_KEY);
            const initialServices = initialValue ? (JSON.parse(initialValue) as CartService[] | null) : [];
            setServices(initialServices);
        } else if (services === null) {
            clearFromLocalStorage(SERVICES_CART_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_KEY, JSON.stringify(services));
        }
    }, [services]);

    const removeService = (uuid: string) => {
        setServices((previous) =>
            previous ? [...previous.filter((service) => service.uuid !== uuid), { uuid: uuid, quantity: 1, checked: false }] : []
        );
    };

    const checkService = (uuid: string) => {
        setServices((previous) => {
            const service = previous?.find((service) => service.uuid === uuid);
            return previous
                ? [
                      ...previous.filter((service) => service.uuid !== uuid),
                      { uuid: uuid, quantity: service?.quantity || 1, checked: true },
                  ]
                : [];
        });
    };

    const toggleService = (uuid: string) => {
        setServices((previous) => {
            const service = previous?.find((service) => service.uuid === uuid);
            return previous
                ? [
                      ...previous.filter((service) => service.uuid !== uuid),
                      { uuid: uuid, quantity: service?.quantity || 1, checked: !service?.checked },
                  ]
                : [];
        });
    };

    const updateServiceAmount = (uuid: string, quantity: number) => {
        setServices((previous) => {
            const service = previous?.find((service) => service.uuid === uuid);
            return previous
                ? [
                      ...previous.filter((service) => service.uuid !== uuid),
                      { uuid: uuid, quantity: quantity, checked: service?.checked ? true : false },
                  ]
                : [];
        });
    };

    const [initialClickedService, setInitialClickedService] = useState<string | null>(null);

    const setInitialServices = (services: { uuid: string; checked?: boolean }[]) => {
        setServices(
            services.map((service) => {
                return {
                    uuid: service.uuid,
                    quantity: 1,
                    checked: service.checked || false,
                };
            })
        );

        setInitialClickedService(services.find((item) => item.checked)?.uuid || null);

        // reset data set in next steps
        setWorker(null);
        setDay(null);
        setTimeSlot(null);
        setObservations(null);
        setPaymentMethod(null);
        setPaymentPhoneNumber(null);
    };

    const resetServices = () => {
        setServices(null);
        setWorker(null);
        setDay(null);
        setTimeSlot(null);
        setObservations(null);
        setScheduleType(null);
        setPaymentMethod(null);
        setPaymentPhoneNumber(null);
    };

    // schedule type
    const [scheduleType, setScheduleType] = useState<'preBook' | 'bookAndPay' | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<'preBook' | 'bookAndPay'>(SERVICES_CART_SCHEDULE_TYPE_KEY);
            setScheduleType(initialValue);
        } else if (scheduleType === null) {
            clearFromLocalStorage(SERVICES_CART_SCHEDULE_TYPE_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_SCHEDULE_TYPE_KEY, scheduleType);
        }
    }, [scheduleType]);

    // workers
    const [displayWorkerStep, setDisplayWorkerStep] = useState<boolean>(false);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_DISPLAY_WORKERS_KEY);
            const initialDisplayWorkerStep = initialValue ? (JSON.parse(initialValue) as boolean) : false;
            setDisplayWorkerStep(initialDisplayWorkerStep);
        } else if (displayWorkerStep === null) {
            clearFromLocalStorage(SERVICES_CART_DISPLAY_WORKERS_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_DISPLAY_WORKERS_KEY, JSON.stringify(displayWorkerStep));
        }
    }, [displayWorkerStep]);

    const [worker, setWorker] = useState<{ uuid: string; name: string } | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_WORKER_KEY);
            const initialWorker = initialValue ? (JSON.parse(initialValue) as Worker | null) : null;
            setWorker(initialWorker ? { uuid: initialWorker.uuid || '', name: initialWorker.name || '' } : null);
        } else if (worker === null) {
            clearFromLocalStorage(SERVICES_CART_WORKER_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_WORKER_KEY, JSON.stringify(worker));
        }
    }, [worker]);

    // schedule
    const [day, setDay] = useState<string | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_SCHEDULE_DAY_KEY);
            setDay(initialValue);
        } else if (day === null) {
            clearFromLocalStorage(SERVICES_CART_SCHEDULE_DAY_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_SCHEDULE_DAY_KEY, day);
        }
    }, [day]);

    const [timeSlot, setTimeSlot] = useState<string | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_SCHEDULE_TIME_KEY);
            setTimeSlot(initialValue);
        } else if (timeSlot === null) {
            clearFromLocalStorage(SERVICES_CART_SCHEDULE_TIME_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_SCHEDULE_TIME_KEY, timeSlot);
        }
    }, [timeSlot]);

    const [observations, setObservations] = useState<string | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_OBSERVATIONS_KEY);
            setObservations(initialValue);
        } else if (observations === null) {
            clearFromLocalStorage(SERVICES_CART_OBSERVATIONS_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_OBSERVATIONS_KEY, observations);
        }
    }, [observations]);

    // payment method
    const [paymentMethod, setPaymentMethod] = useState<string | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_PAYMENT_METHOD_KEY);
            setPaymentMethod(initialValue);
        } else if (paymentMethod === null) {
            clearFromLocalStorage(SERVICES_CART_PAYMENT_METHOD_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_PAYMENT_METHOD_KEY, paymentMethod);
        }
    }, [paymentMethod]);

    const [paymentPhoneNumber, setPaymentPhoneNumber] = useState<string | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            const initialValue = loadFromLocalStorage<string>(SERVICES_CART_PAYMENT_PHONE_NUMBER_KEY);
            setPaymentPhoneNumber(initialValue);
        } else if (paymentPhoneNumber === null) {
            clearFromLocalStorage(SERVICES_CART_PAYMENT_PHONE_NUMBER_KEY);
        } else {
            saveToLocalStorage<string>(SERVICES_CART_PAYMENT_PHONE_NUMBER_KEY, paymentPhoneNumber);
        }
    }, [paymentPhoneNumber]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
        }
    }, []);

    const contextValue: ServicesCartContextProps = {
        storeUuid,
        storeName,
        setStoreUuid,
        setStoreName,
        initialClickedService,
        services,
        checkService,
        toggleService,
        updateServiceAmount,
        removeService,
        setInitialServices,
        resetServices,
        scheduleType,
        setScheduleType,
        displayWorkerStep,
        worker,
        setDisplayWorkerStep,
        setWorker,
        day,
        setDay,
        timeSlot,
        setTimeSlot,
        observations,
        setObservations,
        paymentMethod,
        setPaymentMethod,
        paymentPhoneNumber,
        setPaymentPhoneNumber,
    };

    return <ServicesCartContext.Provider value={contextValue}>{children}</ServicesCartContext.Provider>;
};
